import {
  ApiAccessChartWidget,
  Box,
  ApiChartConfig,
  DropdownOptions,
  Flex,
  Spinner,
  Text,
} from "dash-ui";
import { ApiCredentialsTypesEnum } from "dash-common";
import { useGetUserByApiCredentialsIdQuery } from "gql/generated.graphql";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { RoutesPath } from "flow-components/routes/routes";
import { useEffect, useMemo, useState } from "react";
import { totalApiUsageFetchFunction } from "../helpers/helpers";

const responsiveBoxWidth: [string, string, string, string] = [
  "100%",
  "100%",
  "100%",
  "calc(50% - 8px)",
];

const initialCharts: ApiChartConfig[] = [
  {
    title: "API Access",
    fetchFunction: totalApiUsageFetchFunction({}),
    filters: { type: ApiCredentialsTypesEnum.PLUGIN_USER, appId: null },
  },
];

const ApiPluginUserDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get api credentials from the current url
  const pathname = location.pathname;
  const propertiesParams = matchPath(
    RoutesPath.APIManagementPluginUserDetails,
    pathname
  );
  const apiCredentialsId = propertiesParams?.params?.id;

  const [pluginUserName, setPluginUserName] = useState("");
  const [brokerId, setBrokerId] = useState("");

  const { data, loading, error } = useGetUserByApiCredentialsIdQuery({
    variables: {
      filter: {
        apiCredentialsId: apiCredentialsId ?? "",
        withBrokerInfo: true,
      },
    },
    skip: !apiCredentialsId,
  });

  // Compute charts with the correct appId once data is loaded
  const charts = useMemo<ApiChartConfig[]>(() => {
    const appId =
      data?.getUserByApiCredentialsId?.apiCredentials?.appId ?? null;
    return initialCharts.map((chart) => ({
      ...chart,
      fetchFunction: totalApiUsageFetchFunction(
        chart.filters ? { ...chart.filters, appId } : {}
      ),
      filters: chart.filters ? { ...chart.filters, appId } : undefined,
    }));
  }, [data]);

  useEffect(() => {
    const profile = data?.getUserByApiCredentialsId?.profile;
    if (profile) {
      setPluginUserName(
        profile.lastName
          ? `${profile.firstName} ${profile.lastName}`
          : profile.firstName
      );
    }

    const broker = data?.getUserByApiCredentialsId?.broker;
    if (broker?._id) {
      setBrokerId(broker._id);
    }
  }, [data]);

  return (
    <Box w="100%" p={6}>
      <DropdownOptions />
      {loading ? (
        <Flex w="100%" alignItems="center" justifyContent="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Text color="red">Failed to load Plugin User information.</Text>
      ) : (
        <>
          <Flex mb={8}>
            <Text xl bold>
              {pluginUserName}
            </Text>
          </Flex>
          <Flex gap={16} flexWrap="wrap">
            {charts.map(({ title, fetchFunction }) => (
              <Box w={responsiveBoxWidth} key={title}>
                <ApiAccessChartWidget
                  title={title}
                  fetchFunction={fetchFunction}
                />
              </Box>
            ))}
          </Flex>
          <Flex mt={16}>
            <Box
              as="button"
              type="button"
              className="cursor-pointer"
              style={{ background: "none", border: "none", padding: 0 }}
              onClick={() =>
                navigate(RoutesPath.BrokersDetails.replace(":id", brokerId))
              }
            >
              <Text textDecoration="underline">View plugin user info</Text>
            </Box>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default ApiPluginUserDetailsPage;
